import { PriceObjectDto } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { convertCoachFromDto, convertUserFromDto } from "@modules/profile/profileDtos"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleLightEntity } from "@modules/programBundle/entities/programBundleEntity"
import {
  ItemProgramOnOff,
  ProgramOnOff,
  ProgramOnOffDraft,
  ProgramOnOffStatus,
  ProgramOnOffStripeSubscriptionInfos,
  ProgramOnOffTypeOf,
} from "@modules/programs/programOnOffTypes"
import { ProgramContentTypeOf, ProgramPrice, ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import {
  convertProgramPaymentFromDto,
  convertProgramPriceFromDto,
  ProgramPaymentDto,
} from "@modules/programs/services/programDto"
import {
  ItemProgramInfiniteDtoSchema,
  ProgramInfiniteDraftDto,
  ProgramInfiniteDtoSchema,
} from "@modules/programs/services/programInfiniteDto"
import { convertToEnum } from "@modules/utils/enum"
import { z } from "zod"

export const ItemProgramOnOffDtoSchema = ItemProgramInfiniteDtoSchema.extend({
  duration: z.number(),
  status: z.nativeEnum(ProgramOnOffStatus),
  sessionCount: z.number(),
  sessionIndexes: z.array(z.number()),
})

export type ItemProgramOnOffDto = z.infer<typeof ItemProgramOnOffDtoSchema>

export const ProgramOnOffDtoSchema = ProgramInfiniteDtoSchema.extend({
  duration: z.number(),
  status: z.nativeEnum(ProgramOnOffStatus),
  sessionCount: z.number(),
  sessionIndexes: z.array(z.number()),
  invitationLink: z.string(),
})

export type ProgramOnOffDto = z.infer<typeof ProgramOnOffDtoSchema>

export type ProgramOnOffDraftDto = {
  duration?: number
  status?: string
} & ProgramInfiniteDraftDto



export function convertItemProgramOnOffFromDto(dto: ItemProgramOnOffDto): ItemProgramOnOff {
  return {
    _programType: ProgramOnOffTypeOf,
    id: dto.id,
    title: dto.name,
    price: dto.price as ProgramPrice,
    visibility: dto.visibility,
    image: convertImageFromDto(dto.image),
    author: convertCoachFromDto(dto.owner),
    subscriptionCount: dto.subscriptionCount,
    defaultHideSessionContent: dto.defaultHideSessionContent,
    entranceCheckEnabled: dto.entranceCheckEnabled,
    duration: dto.duration,
    status: dto.status,
    subscribed: dto.subscribed,
    sessionCount: dto.sessionCount,
    sessionIndexes: dto.sessionIndexes,
    subscriptionStatus: dto.subscriptionStatus,
    invitationLink: dto.invitationLink,
    _type: ProgramContentTypeOf,
    sharedCoaches: dto.sharedCoaches?.map((sharedCoach) => ({
      ...sharedCoach,
      user: convertUserFromDto(sharedCoach.user),
    })),
    sharedCoachStatus: dto.sharedCoachStatus,
    obtainableOnlyInBundle: dto.obtainableOnlyInBundle ?? false,
  }
}

export function convertProgramOnOffFromDto(dto: ProgramOnOffDto): ProgramOnOff {
  return {
    ...convertItemProgramOnOffFromDto(dto),
    description: dto.description,
    programBundle: dto.programBundle
      ? ProgramBundleLightEntity.fromDto(dto.programBundle, ProgramBundleType.ON_OFF)
      : undefined,
  }
}

export function convertProgramOnOffDraftToDto(
  draft: ProgramOnOffDraft,
): ProgramOnOffDraftDto & { programIdToDuplicate?: string } {
  return {
    name: draft.name,
    description: draft.description,
    visibility: draft.visibility,
    image: draft.image?.path,
    defaultHideSessionContent: draft.defaultHideSessionContent,
    entranceCheckEnabled: draft.entranceCheckEnabled,
    duration: draft.duration,
    status: draft.status,
    sharedCoaches: draft.sharedCoaches?.map((coach) => coach.user.id),
    programIdToDuplicate: draft.programIdToDuplicate,
    obtainableOnlyInBundle: draft.obtainableOnlyInBundle,
  }
}

export function convertPartialProgramOnOffDraftToDto(draft: Partial<ProgramOnOffDraft>): Partial<ProgramOnOffDraftDto> {
  return {
    name: draft.name ?? undefined,
    description: draft.description ?? undefined,
    visibility: draft.visibility ?? undefined,
    image: draft.image?.path,
    defaultHideSessionContent: draft.defaultHideSessionContent ?? undefined,
    entranceCheckEnabled: draft.entranceCheckEnabled ?? undefined,
    duration: draft.duration,
    status: draft.status,
    sharedCoaches: draft.sharedCoaches?.map((coach) => coach.user.id),
  }
}

export type ProgramOnOffStripeSubscriptionInfosDto = {
  subscriptionId: string
  subscriptionStatus: string
  paidDate?: string
  paidPrice?: PriceObjectDto
  referencePrice?: PriceObjectDto
  payment?: ProgramPaymentDto
}

export function convertProgramOnOffStripeSubscriptionInfosFromDto(
  id: string,
  dto: ProgramOnOffStripeSubscriptionInfosDto,
): ProgramOnOffStripeSubscriptionInfos {
  return {
    id,
    subscriptionId: dto.subscriptionId,
    subscriptionStatus: convertToEnum(
      ProgramSubscriptionStatus,
      dto.subscriptionStatus,
      ProgramSubscriptionStatus.INACTIVE,
    ),
    paidPrice: convertProgramPriceFromDto(dto.paidPrice),
    referencePrice: convertProgramPriceFromDto(dto.referencePrice),
    paidDate: dto.paidDate,
    payment: convertProgramPaymentFromDto(dto.payment),
  }
}
